import './TopicExplorer.scss';

import React, { FC, useContext, useEffect, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import ProgressSpell from 'components/ProgressSpell/ProgressSpell';
import { NavHeadings } from 'constants/Constants';
import { useAIGenerator } from 'customHooks/useAIGenerator';
import { useDataSet } from 'customHooks/useDataSet';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { GeneratedTopicModel } from 'interfaces/GeneratedTopicModel';
import { Document } from 'models/Document';
import { Entities } from 'models/Entities';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { SiteSection } from 'models/SiteSection';
import { TargetKeyword } from 'models/TargetKeyword';
import { Theme } from 'models/Theme';
import { TopicModel } from 'models/TopicModel';
import { useNavigate } from 'react-router-dom';

import ArticleIdeaList from '../ArticleIdeaList/ArticleIdeaList';
import KeywordList from '../KeywordList/KeywordList';
import SetTopic from '../SetTopic/SetTopic';
import SubtopicList from '../SubtopicList/SubtopicList';

interface TopicExplorerProps {
  activeTab: string;
}

const TopicExplorer: FC<TopicExplorerProps> = ({ activeTab }) => {
  const heading: NavHeadingProps = {
    title: 'Topic Explorer',
    ...NavHeadings.TopicExplorer,
  };

  const { selectedProjectId, setIsProjectSelectable, projects, sendLog, notify, configRepo } = useContext(AppContext);
  const onTokenExpired = useTokenExpiredHandler();
  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const [isLoading, setIsLoading] = useState(false);
  const topicGenerator = useAIGenerator(configRepo!.SCHEDULE_API_BASE_URI);
  const navigate = useNavigate();

  const [subtopics, setSubtopics] = React.useState<TopicModel[]>([]);
  const [keywords, setKeywords] = React.useState<TargetKeyword[]>([]);
  const [articleIdeas, setArticleIdeas] = React.useState<GeneratedTopicModel[]>([]);

  const [activeStep, setActiveStep] = useState(1);


  const onGenerateSubtopics = async () => {
    const result = await topicGenerator.getSubdomainsForDomain(selectedProject!);
    sendLog('triggered generate subtopics', LogLevel.info, result);
    setSubtopics(result);
    setActiveStep(2);
  };

  const onIdentifyKeywords = async (filteredSubtopics: TopicModel[]) => {
    if (filteredSubtopics.length === 0) {
      sendLog('No subtopics selected for keyword identification', LogLevel.warn);
      notify('No subtopics selected for keyword identification', NotificationType.Warning);
    } else {
      try {
        const result = await topicGenerator.tryGetKeywordOpportunitiesOrDefault(selectedProject!, filteredSubtopics);
        if (result?.length === 0) {
          notify('Failed to find keyword opportunities for the selected subtopics.  Please select more subtopics and try again.', NotificationType.Error);
        } else {
          setKeywords(result);
          sendLog('triggered identify keywords', LogLevel.info, result);
          setActiveStep(3);
        }
      } catch (e: any) {
        sendLog(`Failed to generate keyword opportunities, error: ${e.message}`, LogLevel.error, e);
        notify('Failed to generate keyword opportunities, please retry.', NotificationType.Error);
      }
    }
  };

  const onSelectKeywords = async (filteredKeywords: TargetKeyword[], filteredSubtopics: TopicModel[]) => {
    if (filteredKeywords.length === 0) {
      sendLog('No keywords selected for article idea generation', LogLevel.warn);
      notify('No keywords selected for article idea generation', NotificationType.Warning);
    } else if (filteredSubtopics.length === 0) {
      sendLog('No subtopics selected for article idea generation', LogLevel.warn);
      notify('No subtopics selected for article idea generation', NotificationType.Warning);
    } else {
      const result = await topicGenerator.generateArticleIdeas(selectedProject!, filteredSubtopics, undefined, filteredKeywords);
      setArticleIdeas(result);
      sendLog('triggered generate article ideas', LogLevel.info, result);
      setActiveStep(4);
    }
  };

  const { executeEntityAction } = useModifyEntityService<Theme>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.Themes,
    sendLog,
    onTokenExpired,
  );

  const { dataSet: sectionsDataSet } = useDataSet<SiteSection>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.SiteSections,
    selectedProjectId,
  );
  const sections: SiteSection[] = sectionsDataSet.responseData as SiteSection[];

  const onSaveSelectedTopics = async (themes: GeneratedTopicModel[]) => {
    const themeBatch = themes.map((theme) => {
      const themeEntry: Theme = {
        name: theme.theme,
        description: theme.themeDescription,
        projectId: selectedProjectId!,
        scheduledDate: theme.month,
      };

      themeEntry.documents = theme.articles.map((article) => ({
        content: '',
        statusId: 1,
        projectId: selectedProjectId!,
        siteSectionId: sections.find(s => s.sectionName === article.categoryName)?.id,
        title: article.title,
        shortDescription: article.subTitle,
        plannedDate: article.plannedDate,
        targetLength: 500,
        keywordId: article.keywordId,
      } as Document));

      return themeEntry;
    });

    const result = await executeEntityAction("Batch", HttpMethod.POST, themeBatch);
    if (!result) {
      sendLog(`Failed to create theme set`, LogLevel.error, themeBatch);
      notify('Failed to create theme set, please try again or select less articles at a time and retry', NotificationType.Error);
    } else {
      navigate('/documents');
    }

  };

  const onSelectArticleIdeas = async (selectedArticles: GeneratedTopicModel[]) => {
    if (selectedArticles.length === 0) {
      sendLog('No article ideas selected', LogLevel.warn);
      notify('No article ideas selected', NotificationType.Warning);
    } else {
      sendLog('triggered save articles to DB', LogLevel.info, selectedArticles);
      await onSaveSelectedTopics(selectedArticles);
    }
  };

  useEffect(() => {
    setIsProjectSelectable(false);
  });


  if (!selectedProject) {
    return <div className="TopicExplorer">
      <h1>No Project Selected </h1>
      <p>No project selected, please select a project and refresh this page to continue.</p>
    </div>;
  }

  const getStatusIcon = (stepNumber: number) => {
    if (activeStep > stepNumber) {
      return (<i className='bi bi-check-square' style={{ marginRight: '.5em', color: 'green' }} />)
    }
    return (<i className='bi bi-square' style={{ marginRight: '.5em', color: '#CCC' }} />)
  }

  return (
    <div className="TopicExplorer" style={{ position: 'relative' }}>
      <NavHeading {...heading} />

      <ProgressSpell isVisible={isLoading} spellType="Topic Authority" />

      <div className="step-indicator">
        <div className={`step ${activeStep === 1 ? 'active' : ''}`}>
          {getStatusIcon(1)}
          1. Set Primary Topic
        </div>
        <div className={`step ${activeStep === 2 ? 'active' : ''}`}>
          {getStatusIcon(2)}
          2. Generate Subtopics
        </div>
        <div className={`step ${activeStep === 3 ? 'active' : ''}`}>
          {getStatusIcon(3)}
          3. Identify Keywords
        </div>
        <div className={`step ${activeStep === 4 ? 'active' : ''}`}>
          {getStatusIcon(4)}
          4. Generate Article Ideas
        </div>
      </div>

      <SetTopic
        selectedProject={selectedProject}
        onGenerateSubtopics={onGenerateSubtopics}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        activeStep={activeStep} />

      <SubtopicList
        onIdentifyKeywords={onIdentifyKeywords}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        subtopics={subtopics}
        activeTab={activeTab}
        activeStep={activeStep} />

      <KeywordList
        onSelectKeywords={onSelectKeywords}
        selectedSubtopics={subtopics}
        keywords={keywords}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        activeStep={activeStep} />

      <ArticleIdeaList
        onSelectArticleIdeas={onSelectArticleIdeas}
        articleIdeas={articleIdeas}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        activeStep={activeStep} />

    </div>
  );
}

export default TopicExplorer;
